import React, { useCallback, useMemo, useState } from 'react';
import {
  RowCentered,
  SectionHeaderRowSpaceBetween,
  SectionHeaderRowSpaceBetweenNoBorder,
} from '@ydistri/ds';
import { TemplateSelect } from './TemplateSelect';
import { Dropdown, DropdownProps, Space } from 'antd';
import { icons, IconType } from '../../components/icons/icons';
import { COLORS } from '../../styles/style';
import TemplatePotentialOverview from '../../components/global/HeaderPotentialOverview/TemplatePotentialOverview';
import EditTemplateModal from './EditTemplateModal';
import { generatePartialPath, RoutingPage } from '../../routes/routerSlice';
import { ROUTES } from '../../components/menu/getMenuLeftItems';
import { useNavigate } from 'react-router';
import useSubpage from '../../hooks/useSubpage';
import CreateTemplateModal from './CreateTemplateModal';
import DeleteTemplateModal from './DeleteTemplateModal';
import { ConfigurationSubpage } from './Configuration';
import MenuSubpages from '../../components/menu/MenuSubpages';
import ConfigurationResetButton from './ConfigurationCategories/ConfigurationResetButton';
import MoreIconButton from '../../components/buttons/MoreIconButton';
import { CalculationDetailSubpages } from '../Calculations/calculationsLib';
import { ProjectAdministrationSubpage } from '../ProjectAdministration/projectAdministrationLib';
import { useValidator } from './Validator/useValidator';
import { resetState } from '../../components/global/CategoryTree/categoryTreeSlice';
import { useApplicationData } from '../../hooks/useApplicationData';
import ConfigurationExceptions from './ConfigurationCategories/ConfigurationExceptions';
import { useUser } from '../../hooks/useUser';
import { useAppDispatch } from '../../store';
import { useStableParams } from '../../routes/useStableParams';
import useIsConfigurationEditable from '../../hooks/useIsConfigurationEditable';
import { usePatchTemplateMutation } from './apiTemplate';
import { addToast } from '@ydistri/utils';
import { useGetCalculationConfigurationQuery } from '../../apis/apiGlobalConfiguration';
import TemplateLockedAlert from './components/TemplateLockedAlert';
import { ROOT_CATEGORY_ID } from '../../components/global/_constants';
import { useTemplateOrCalculationWithValue } from '../../hooks/useTemplateOrCalculationWithValue';

enum TemplateAction {
  EDIT = 'EDIT',
  DELETE = 'DELETE',
  CREATE = 'CREATE',
}

export interface SubmenuOption {
  value: ConfigurationSubpage | CalculationDetailSubpages | ProjectAdministrationSubpage;
  label: string;
  appendDivider?: boolean;
  isVisible?: boolean;
  disabled?: boolean;
}

const ConfigurationHeader: React.FC = () => {
  const [openedActionModal, setOpenedActionModal] = useState<TemplateAction>();
  const navigate = useNavigate();
  const user = useUser();
  const subpage = useSubpage();
  const dispatch = useAppDispatch();
  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const { isLoading: isValidatorLoading } = useValidator(templateOrCalculation);
  const { departments: hasDepartments } = useApplicationData();

  const { projectShortName, templateId } = useStableParams();

  const { data: calculationConfig, isFetching: isCalculationConfigFetching } =
    useGetCalculationConfigurationQuery(templateOrCalculation.value);
  const isConfigurationEditable = useIsConfigurationEditable();

  const [editTemplate] = usePatchTemplateMutation();

  const lockTemplate = useCallback(() => {
    if (templateOrCalculation.hasValidValue) {
      editTemplate({
        templateId: templateOrCalculation.id,
        data: { isLocked: true },
      }).then(newData => {
        if (newData.data?.isLocked === true) {
          dispatch(addToast({ message: 'Template has been locked' }));
        }
      });
    }
  }, [dispatch, editTemplate, templateOrCalculation.hasValidValue, templateOrCalculation.id]);

  const unlockTemplate = useCallback(() => {
    if (templateOrCalculation.hasValidValue) {
      editTemplate({
        templateId: templateOrCalculation.id,
        data: { isLocked: false },
      }).then(newData => {
        if (newData.data?.isLocked === false) {
          dispatch(addToast({ message: 'Template has been unlocked' }));
        }
      });
    }
  }, [dispatch, editTemplate, templateOrCalculation.hasValidValue, templateOrCalculation.id]);

  const templateOptionDropdownItems: DropdownProps['menu'] = useMemo(() => {
    const result: DropdownProps['menu'] = {
      items: [
        {
          label: 'Edit current template',
          key: '1',
          icon: icons[IconType.EDIT_OUTLINED],
          onClick: () => {
            setOpenedActionModal(TemplateAction.EDIT);
          },
          style: {
            backgroundColor: COLORS.PRIMARY_LIGHT,
          },
          disabled: !isConfigurationEditable,
        },
      ],
    };

    //options to Lock / Unlock the template only for superusers
    if (user?.isSuperUser && !isCalculationConfigFetching && calculationConfig) {
      if (calculationConfig.isLocked) {
        result.items?.push({
          label: 'Unlock template',
          key: '4',
          icon: icons[IconType.UNLOCK_OUTLINED],
          onClick: unlockTemplate,
        });
      } else {
        result.items?.push({
          label: 'Lock template',
          key: '4',
          icon: icons[IconType.LOCK_OUTLINED],
          onClick: lockTemplate,
        });
      }
    }

    result.items?.push(
      {
        label: 'Create new',
        key: '2',
        icon: icons[IconType.PLUS_OUTLINED],
        onClick: () => {
          setOpenedActionModal(TemplateAction.CREATE);
        },
      },
      {
        type: 'divider',
      },
      {
        label: 'Delete',
        key: '3',
        icon: icons[IconType.DELETE_OUTLINED],
        onClick: () => {
          setOpenedActionModal(TemplateAction.DELETE);
        },
        danger: true,
        disabled: !isConfigurationEditable,
      },
    );

    return result;
  }, [
    calculationConfig,
    isCalculationConfigFetching,
    isConfigurationEditable,
    lockTemplate,
    unlockTemplate,
    user?.isSuperUser,
  ]);

  const submenuOptions: SubmenuOption[] = useMemo(() => {
    const options: SubmenuOption[] = [
      {
        value: ConfigurationSubpage.CATEGORIES,
        label: 'Categories',
      },
      {
        value: ConfigurationSubpage.REGIONS,
        label: 'Regions',
      },
      {
        value: ConfigurationSubpage.STORES,
        label: hasDepartments ? 'Stores and departments' : 'Stores',
      },
      {
        value: ConfigurationSubpage.SKUCLASSES,
        label: 'SKU classes',
      },
      {
        value: ConfigurationSubpage.PRODUCTLISTS,
        label: 'Product lists',
      },
      {
        value: ConfigurationSubpage.BRANDS,
        label: 'Brands',
        appendDivider: !user?.isSuperUser,
      },
    ];

    if (user?.isSuperUser) {
      options.push({
        value: ConfigurationSubpage.ADVANCED,
        label: 'Advanced',
        appendDivider: true,
      });
    }

    options.push({
      value: ConfigurationSubpage.DRILLDOWN,
      label: 'Drill down',
    });

    return options;
  }, [hasDepartments, user?.isSuperUser]);

  const closeOpenedActionModal = useCallback(() => {
    setOpenedActionModal(undefined);
  }, []);

  const dropdownTrigger: DropdownProps['trigger'] = useMemo(() => ['click'], []);
  const dropdownAlign: DropdownProps['align'] = useMemo(() => ({ offset: [0, 5] }), []);

  const onChangeTemplateHandler = useCallback(
    (value: number) => {
      //TODO change template!!!
      const templateId = value.toString();
      dispatch(resetState());
      navigate(
        generatePartialPath(
          ROUTES.configurationChildRoute,
          { projectShortName, slug: `${ROOT_CATEGORY_ID}`, templateId },
          ConfigurationSubpage.CATEGORIES,
        ),
      );
    },
    [dispatch, navigate, projectShortName],
  );

  const openCreateModalHandler = useCallback(() => {
    setOpenedActionModal(TemplateAction.CREATE);
  }, []);

  return (
    <>
      <SectionHeaderRowSpaceBetween>
        <RowCentered $gap="0.5rem">
          <TemplateSelect
            onChange={onChangeTemplateHandler}
            templateId={templateId ? parseInt(templateId) : undefined}
            createButtonAction={openCreateModalHandler}
            variant="grey"
            loading={isValidatorLoading}
          />
          <Dropdown
            trigger={dropdownTrigger}
            menu={templateOptionDropdownItems}
            align={dropdownAlign}
          >
            <div>
              <MoreIconButton />
            </div>
          </Dropdown>
          <TemplateLockedAlert />
        </RowCentered>
        <RowCentered $padding={1}>
          <Space>
            <TemplatePotentialOverview />
          </Space>
        </RowCentered>
      </SectionHeaderRowSpaceBetween>
      <SectionHeaderRowSpaceBetweenNoBorder>
        <RowCentered $gap="1rem">
          <MenuSubpages
            routingPage={RoutingPage.CONFIGURATION}
            submenuOptions={submenuOptions}
            selectedOption={subpage}
          />
        </RowCentered>
        {subpage === ConfigurationSubpage.CATEGORIES && (
          <RowCentered $gap="1rem">
            <ConfigurationExceptions />
            <ConfigurationResetButton />
          </RowCentered>
        )}
      </SectionHeaderRowSpaceBetweenNoBorder>
      {openedActionModal === TemplateAction.EDIT && (
        <EditTemplateModal
          open={true}
          onCancel={closeOpenedActionModal}
          templateId={templateOrCalculation.id}
        />
      )}
      {openedActionModal === TemplateAction.CREATE && (
        <CreateTemplateModal
          open={true}
          onCancel={closeOpenedActionModal}
          templateId={templateOrCalculation.id}
        />
      )}
      {openedActionModal === TemplateAction.DELETE && (
        <DeleteTemplateModal
          onCancel={closeOpenedActionModal}
          templateId={templateOrCalculation.id}
        />
      )}
    </>
  );
};

export default ConfigurationHeader;
